import React, { useMemo, useState, useRef, useEffect } from 'react';
import CommentThreadDisplay from './CommentThreadDisplay';
import EstimatSubItemRow from './EstimatSubItemRow';
import { calculateItemTotal } from '../../domain/calculations';

/**
 * EstimatItemRow - Renderar en enskild item-rad i estimat-tabellen
 * Hanterar både standard items, subcategory items och custom items
 * 
 * 🚀 OPTIMERING: Använder useMemo för att cacha beräkningar (denna komponent renderas många gånger)
 * 
 * @param {Object} item - Item-definition från config
 * @param {Object} estimatItem - Estimat-data för denna item
 * @param {string} categoryKey - Kategori-nyckel (t.ex. 'byggoffert')
 * @param {string} subcategoryKey - Underkategori-nyckel (om tillämpligt)
 * @param {Object} customerComments - Kundens kommentarer
 * @param {Object} byggkonsultResponses - Byggkonsultens svar
 * @param {Object} showInlineComments - State för vilka kommentarer som visas
 * @param {Object} editingLabels - State för vilka labels som redigeras
 * @param {Object} tempLabelValues - Temporära label-värden under redigering
 * @param {Object} editingPrices - State för vilka priser som redigeras
 * @param {Object} tempPriceValues - Temporära pris-värden under redigering
 * @param {Function} updateEstimat - Funktion för att uppdatera estimat-data
 * @param {Function} handleRemoveItem - Funktion för att ta bort item
 * @param {Function} startEditLabel - Funktion för att starta label-redigering
 * @param {Function} changeTempLabel - Funktion för att ändra temporär label
 * @param {Function} autoSaveLabel - Funktion för att auto-spara label
 * @param {Function} startEditPrice - Funktion för att starta pris-redigering
 * @param {Function} changeTempPrice - Funktion för att ändra temporärt pris
 * @param {Function} autoSavePrice - Funktion för att auto-spara pris
 * @param {Function} toggleInlineComment - Funktion för att toggla inline-kommentar
 * @param {boolean} isSubItemsExpanded - Om underrader är expanderade för detta item
 * @param {Function} toggleSubItemsExpanded - Funktion för att toggla expand/collapse av underrader
 * @param {Function} addSubItem - Funktion för att lägga till underrad
 * @param {Function} updateSubItem - Funktion för att uppdatera underrad
 * @param {Function} removeSubItem - Funktion för att ta bort underrad
 * @param {Function} toggleUseSubItemsTotal - Funktion för att växla prisläge för underrader
 */
const EstimatItemRow = React.forwardRef(({
  item,
  estimatItem,
  categoryKey,
  subcategoryKey,
  itemKey,
  customerComments,
  byggkonsultResponses,
  showInlineComments,
  editingLabels,
  tempLabelValues,
  editingPrices,
  tempPriceValues,
  updateEstimat,
  handleRemoveItem,
  startEditLabel,
  changeTempLabel,
  autoSaveLabel,
  startEditPrice,
  changeTempPrice,
  autoSavePrice,
  toggleInlineComment,
  handleEditByggkonsultEntry,
  onOpenInternalNoteModal,
  getInternalNote,
  hasInternalNote,
  // SubItems props
  isSubItemsExpanded,
  toggleSubItemsExpanded,
  addSubItem,
  updateSubItem,
  removeSubItem,
  toggleUseSubItemsTotal,
  // Drag & drop props
  dragHandleProps,
  isDragging,
  ...restProps
}, ref) => {
  // State för intern förklaring popover
  const [showInternalNotePopover, setShowInternalNotePopover] = useState(false);
  const popoverRef = useRef(null);
  const iconRef = useRef(null);
  
  // Beräkna global itemKey för intern förklaring
  // Denna nyckel används för att lagra förklaringen globalt (delas mellan alla estimat)
  const globalItemKey = useMemo(() => {
    return subcategoryKey 
      ? `${categoryKey}_${subcategoryKey}_${item.name}`
      : `${categoryKey}_${item.name}`;
  }, [categoryKey, subcategoryKey, item.name]);
  
  // Hämta global intern förklaring
  const internalNote = useMemo(() => {
    return getInternalNote ? getInternalNote(globalItemKey) : '';
  }, [getInternalNote, globalItemKey]);
  
  const itemHasInternalNote = useMemo(() => {
    return hasInternalNote ? hasInternalNote(globalItemKey) : false;
  }, [hasInternalNote, globalItemKey]);

  // Stäng popover vid klick utanför
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popoverRef.current && 
        !popoverRef.current.contains(event.target) &&
        iconRef.current &&
        !iconRef.current.contains(event.target)
      ) {
        setShowInternalNotePopover(false);
      }
    };

    if (showInternalNotePopover) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showInternalNotePopover]);
  // 🚀 OPTIMERING: Cacha total beräkning
  // Använder calculateItemTotal för att inkludera subItems
  // OBS: Vi måste inkludera subItems i dependency för att fånga ändringar i underrader
  const total = useMemo(() => {
    return calculateItemTotal(estimatItem);
  }, [estimatItem]);

  // Kolla om detta item har subItems
  const hasSubItems = useMemo(() => {
    return estimatItem.subItems && estimatItem.subItems.length > 0;
  }, [estimatItem.subItems]);

  // Antal aktiva subItems
  const activeSubItemsCount = useMemo(() => {
    if (!estimatItem.subItems) return 0;
    return estimatItem.subItems.filter(sub => sub.selected && sub.quantity > 0).length;
  }, [estimatItem.subItems]);

  // Antal från "Stommar" subItem (för visning när useSubItemsTotal är aktivt)
  // Letar efter subItem med label "Stommar" (case-insensitive)
  const stommarQuantity = useMemo(() => {
    if (!estimatItem.subItems || !estimatItem.useSubItemsTotal) return null;
    const stommarItem = estimatItem.subItems.find(
      sub => sub.label && sub.label.toLowerCase().includes('stomm')
    );
    return stommarItem?.quantity || null;
  }, [estimatItem.subItems, estimatItem.useSubItemsTotal]);
  
  // 🚀 OPTIMERING: Cacha alla nycklar
  const { labelKey, formItemKey, priceKey, commentKey } = useMemo(() => ({
    fullKey: subcategoryKey 
      ? `${categoryKey}_${subcategoryKey}_${item.name}`
      : `${categoryKey}_${item.name}`,
    labelKey: subcategoryKey 
      ? `${categoryKey}_${subcategoryKey}_${item.name}`
      : `${categoryKey}_${item.name}`,
    formItemKey: subcategoryKey ? `${subcategoryKey}_${item.name}` : item.name,
    priceKey: subcategoryKey 
      ? `${categoryKey}_${subcategoryKey}_${item.name}_price`
      : `${categoryKey}_${item.name}_price`,
    commentKey: itemKey
  }), [categoryKey, subcategoryKey, item.name, itemKey]);
  
  // 🚀 OPTIMERING: Cacha display label
  const displayLabel = useMemo(
    () => (typeof estimatItem.label === 'string' && estimatItem.label.length >= 0) 
      ? estimatItem.label 
      : (item.label || item.name),
    [estimatItem.label, item.label, item.name]
  );
  
  // 🚀 OPTIMERING: Cacha isCustom check
  const isCustom = useMemo(
    () => Boolean(estimatItem.isCustom) || (item.name && item.name.startsWith('custom_')),
    [estimatItem.isCustom, item.name]
  );
  
  return (
    <>
      <tr ref={ref} {...restProps} className={`${estimatItem.selected ? 'bg-blue-50' : ''} ${isDragging ? 'shadow-lg opacity-90 bg-white' : ''}`}>
        {/* Ändra-kolumn */}
        <td className="px-1 sm:px-2 py-2 sm:py-3 text-center align-middle whitespace-nowrap">
          <div className="flex items-center justify-center gap-0.5">
            {dragHandleProps && (
              <span {...dragHandleProps} className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 p-0.5" title="Dra för att flytta">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M8 6a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm8-16a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4z"/></svg>
              </span>
            )}
          <button
            onClick={() => startEditLabel(labelKey, displayLabel)}
            className="text-blue-600 hover:text-blue-800 p-1 rounded focus:outline-none focus:ring-2 focus:ring-leadme-pink"
            aria-label={`Aktivera redigering för ${displayLabel}`}
            title="Redigera"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
              <path d="M5 15.5V19h3.5l10-10-3.5-3.5-10 10z" />
            </svg>
          </button>
          {isCustom && (
            <button
              onClick={() => handleRemoveItem(categoryKey, formItemKey)}
              className="text-red-600 hover:text-red-800 p-1 rounded focus:outline-none focus:ring-2 focus:ring-red-300 ml-0.5"
              aria-label="Ta bort rad"
              title="Ta bort rad"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M9 3a1 1 0 00-1 1v1H5a1 1 0 000 2h14a1 1 0 100-2h-3V4a1 1 0 00-1-1H9zm-3 6a1 1 0 011 1v8a2 2 0 002 2h6a2 2 0 002-2V10a1 1 0 112 0v8a4 4 0 01-4 4H9a4 4 0 01-4-4v-8a1 1 0 011-1zm3 1a1 1 0 012 0v8a1 1 0 11-2 0v-8zm6 0a1 1 0 10-2 0v8a1 1 0 102 0v-8z" clipRule="evenodd" />
              </svg>
            </button>
          )}
          </div>
        </td>
        
        {/* Arbetsmoment */}
        <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-900">
          {editingLabels[labelKey] ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={tempLabelValues[labelKey] ?? displayLabel}
                onChange={(e) => changeTempLabel(labelKey, e.target.value)}
                aria-label={`Redigera arbetsmoment ${item.name}`}
                className="w-full px-1.5 sm:px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-leadme-pink focus:border-leadme-pink bg-white"
                onBlur={() => autoSaveLabel(categoryKey, formItemKey, labelKey)}
                onKeyDown={(e) => { 
                  if (e.key === 'Enter') { 
                    e.preventDefault(); 
                    autoSaveLabel(categoryKey, formItemKey, labelKey); 
                  }
                }}
                autoFocus
              />
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {/* Expand/collapse chevron för subItems */}
              {hasSubItems && (
                <button
                  onClick={() => toggleSubItemsExpanded(categoryKey, formItemKey)}
                  className="text-gray-500 hover:text-gray-700 p-0.5 rounded focus:outline-none focus:ring-2 focus:ring-leadme-pink"
                  aria-label={isSubItemsExpanded ? 'Dölj underrader' : 'Visa underrader'}
                  title={isSubItemsExpanded ? 'Dölj underrader' : 'Visa underrader'}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className={`w-4 h-4 transition-transform ${isSubItemsExpanded ? 'rotate-90' : ''}`}
                  >
                    <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
              <span>
                {displayLabel}
                {hasSubItems && (
                  <span className="ml-1 text-xs text-gray-500">
                    ({activeSubItemsCount})
                  </span>
                )}
              </span>
              {/* Intern förklaring info-ikon */}
              <div className="relative">
                <button
                  ref={iconRef}
                  onClick={() => setShowInternalNotePopover(!showInternalNotePopover)}
                  onMouseEnter={() => setShowInternalNotePopover(true)}
                  className={`p-0.5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-leadme-pink ${
                    itemHasInternalNote 
                      ? 'text-blue-600 hover:text-blue-800 bg-blue-100' 
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                  aria-label={itemHasInternalNote ? 'Visa intern förklaring' : 'Lägg till intern förklaring'}
                  title={itemHasInternalNote ? 'Intern förklaring finns' : 'Lägg till intern förklaring'}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
                  </svg>
                </button>
                
                {/* Popover */}
                {showInternalNotePopover && (
                  <div 
                    ref={popoverRef}
                    onMouseLeave={() => setShowInternalNotePopover(false)}
                    className="absolute z-50 left-0 top-full mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-3"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Intern förklaring</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowInternalNotePopover(false);
                          if (onOpenInternalNoteModal) {
                            onOpenInternalNoteModal({
                              globalItemKey,
                              itemLabel: displayLabel,
                              currentNote: internalNote
                            });
                          }
                        }}
                        className="text-blue-600 hover:text-blue-800 p-1 rounded focus:outline-none focus:ring-2 focus:ring-leadme-pink"
                        aria-label="Redigera intern förklaring"
                        title="Redigera"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                          <path d="M5.433 13.917l1.262-3.155A4 4 0 017.58 9.42l6.92-6.918a2.121 2.121 0 013 3l-6.92 6.918c-.383.383-.84.685-1.343.886l-3.154 1.262a.5.5 0 01-.65-.65z" />
                          <path d="M3.5 5.75c0-.69.56-1.25 1.25-1.25H10A.75.75 0 0010 3H4.75A2.75 2.75 0 002 5.75v9.5A2.75 2.75 0 004.75 18h9.5A2.75 2.75 0 0017 15.25V10a.75.75 0 00-1.5 0v5.25c0 .69-.56 1.25-1.25 1.25h-9.5c-.69 0-1.25-.56-1.25-1.25v-9.5z" />
                        </svg>
                      </button>
                    </div>
                    <p className={`text-sm ${internalNote ? 'text-gray-700' : 'text-gray-400 italic'}`}>
                      {internalNote || 'Ingen förklaring tillagd'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </td>
        
        {/* Välj */}
        <td className="px-2 sm:px-4 py-2 sm:py-3 text-left whitespace-nowrap">
          <input
            type="checkbox"
            checked={estimatItem.selected}
            onChange={(e) => updateEstimat(categoryKey, formItemKey, 'selected', e.target.checked)}
            className="rounded border-gray-300 text-leadme-pink focus:ring-leadme-pink"
          />
        </td>
        
        {/* A-pris */}
        <td className="px-2 sm:px-4 py-2 sm:py-3 text-center text-xs sm:text-sm text-gray-900 whitespace-nowrap">
          {/* Om useSubItemsTotal är true, visa "-" eftersom priset beräknas från underrader */}
          {estimatItem.useSubItemsTotal && hasSubItems ? (
            <span className="text-gray-400 italic">från underrader</span>
          ) : editingPrices[priceKey] ? (
            <input
              type="number"
              min="0"
              step="1"
              value={tempPriceValues[priceKey] ?? String(typeof estimatItem.price === 'object' ? (estimatItem.price?.totalt || 0) : (estimatItem.price ?? 0))}
              onChange={(e) => changeTempPrice(priceKey, e.target.value)}
              onBlur={() => autoSavePrice(categoryKey, formItemKey, priceKey)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  autoSavePrice(categoryKey, formItemKey, priceKey);
                }
              }}
              aria-label={`Redigera pris för ${item.name}`}
              className="w-full max-w-[5rem] sm:max-w-[7rem] px-2 py-1 text-right text-xs sm:text-sm border border-gray-300 rounded focus:ring-1 focus:ring-leadme-pink focus:border-leadme-pink"
            />
          ) : (
            <div className="flex items-center justify-center gap-2">
              <span>{(typeof estimatItem.price === 'object' ? (estimatItem.price?.totalt || 0) : estimatItem.price).toLocaleString('sv-SE')} kr</span>
              <button
                onClick={() => startEditPrice(priceKey, typeof estimatItem.price === 'object' ? (estimatItem.price?.totalt || 0) : estimatItem.price)}
                className="text-blue-600 hover:text-blue-800 p-1 rounded focus:outline-none focus:ring-2 focus:ring-leadme-pink"
                aria-label={`Aktivera prisredigering för ${item.name}`}
                title="Redigera pris"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                  <path d="M5 15.5V19h3.5l10-10-3.5-3.5-10 10z" />
                </svg>
              </button>
            </div>
          )}
        </td>
        
        {/* Antal */}
        <td className="px-2 sm:px-4 py-2 sm:py-3 text-center whitespace-nowrap">
          {/* Om useSubItemsTotal och vi har stommar, visa det antalet (skrivskyddat) */}
          {estimatItem.useSubItemsTotal && stommarQuantity !== null ? (
            <span className="text-gray-600" title="Baserat på antal stommar">
              {stommarQuantity}
            </span>
          ) : (
            <input
              type="number"
              min="0"
              value={estimatItem.quantity}
              onChange={(e) => updateEstimat(categoryKey, formItemKey, 'quantity', parseFloat(e.target.value) || 0)}
              onFocus={(e) => e.target.select()}
              className="w-12 sm:w-16 px-2 py-1 text-center text-xs sm:text-sm border border-gray-300 rounded focus:ring-1 focus:ring-leadme-pink focus:border-leadme-pink"
              disabled={!estimatItem.selected}
            />
          )}
        </td>
        
        {/* Enhet */}
        <td className="px-2 sm:px-4 py-2 sm:py-3 text-center text-xs sm:text-sm text-gray-900 whitespace-nowrap">
          {isCustom ? (
            <select
              value={estimatItem.unit || 'st'}
              onChange={(e) => updateEstimat(categoryKey, formItemKey, 'unit', e.target.value)}
              className="px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-leadme-pink focus:border-leadme-pink bg-white"
              aria-label="Ändra enhet"
            >
              <option value="h">h</option>
              <option value="st">st</option>
              <option value="lpm">lpm</option>
              <option value="kvm">kvm</option>
            </select>
          ) : (
            estimatItem.unit
          )}
        </td>
        
        {/* Summa */}
        <td className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm text-gray-900">
          {(() => {
            if (estimatItem.selected && total === 0) {
              return <span className="italic text-gray-600">preliminärt</span>;
            }
            return total > 0 ? `${total.toLocaleString('sv-SE')} kr` : '-';
          })()}
        </td>
        
        {/* Kommentarer — dold på mobil */}
        <td className="p-0 sm:px-4 sm:py-3 text-center whitespace-nowrap w-0 max-w-0 sm:max-w-none sm:w-auto overflow-hidden">
          <div className="hidden sm:block">
          {(() => {
            const hasCustomerComment = Boolean(customerComments[commentKey]);
            const hasConsultantComment = Boolean(byggkonsultResponses[commentKey]);
            const hasAnyComment = hasCustomerComment || hasConsultantComment;

            if (hasAnyComment) {
              return (
                <div className="flex items-center justify-start gap-2">
                  <span className="relative inline-block group">
                    <button
                      onClick={() => toggleInlineComment(commentKey)}
                      className="text-blue-600 hover:text-blue-800 text-xs underline"
                      aria-label={(() => {
                        const v = customerComments[commentKey];
                        const name = (typeof v === 'object' && v?.author?.name) ? v.author.name : null;
                        return name ? `Kommentar från ${name}` : 'Visa kommentar';
                      })()}
                    >
                      {showInlineComments[commentKey] ? 'Dölj' : (hasCustomerComment ? 'Visa kommentar' : 'Visa anteckning')}
                    </button>
                    {(() => {
                      const v = customerComments[commentKey];
                      const name = (typeof v === 'object' && v?.author?.name) ? v.author.name : null;
                      return name;
                    })() && (
                      <span className="pointer-events-none absolute -top-7 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                        Kommentar från {(() => {
                          const v = customerComments[commentKey];
                          return (typeof v === 'object' && v?.author?.name) ? v.author.name : '';
                        })()}
                      </span>
                    )}
                  </span>
                  {/* Rosa prick för kundkommentar, blå för byggkonsult-anteckning */}
                  <div
                    className={`w-2 h-2 rounded-full animate-pulse ${hasCustomerComment ? 'bg-leadme-pink' : 'bg-blue-500'}`}
                    title={hasCustomerComment ? 'Har kundkommentar' : 'Har byggkonsult-anteckning'}
                  />
                </div>
              );
            }
            return <span className="text-gray-400 text-xs">-</span>;
          })()}
          </div>
        </td>
      </tr>
      
      {/* Inline kommentarsrad */}
      {showInlineComments[commentKey] && (
        <tr className="bg-blue-50">
          <td colSpan="8" className="px-2 sm:px-4 py-2 sm:py-3">
            <CommentThreadDisplay
              customerComments={customerComments[commentKey]}
              byggkonsultResponses={byggkonsultResponses[commentKey]}
              displayName={displayLabel}
              commentKey={commentKey}
              handleEditByggkonsultEntry={handleEditByggkonsultEntry}
            />
          </td>
        </tr>
      )}

      {/* SubItems - underrader (endast om expanderade) */}
      {isSubItemsExpanded && hasSubItems && (
        <>
          {estimatItem.subItems.map((subItem) => (
            <EstimatSubItemRow
              key={subItem.id}
              subItem={subItem}
              categoryKey={categoryKey}
              itemName={formItemKey}
              updateSubItem={updateSubItem}
              removeSubItem={removeSubItem}
            />
          ))}
          {/* Lägg till underrad-knapp */}
          <tr className="bg-gray-50 border-l-4 border-blue-200">
            <td colSpan="8" className="px-2 sm:px-4 py-2">
              <div className="flex items-center gap-4 pl-2 sm:pl-4">
                <button
                  onClick={() => addSubItem(categoryKey, formItemKey)}
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm focus:outline-none focus:ring-2 focus:ring-leadme-pink rounded px-2 py-1"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                    <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
                  </svg>
                  Lägg till underrad
                </button>

                {/* Toggle för useSubItemsTotal */}
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={estimatItem.useSubItemsTotal || false}
                      onChange={() => toggleUseSubItemsTotal(categoryKey, formItemKey)}
                      className="rounded border-gray-300 text-leadme-pink focus:ring-leadme-pink w-3 h-3"
                    />
                    <span>Endast underradernas summa</span>
                  </label>
                </div>
              </div>
            </td>
          </tr>
        </>
      )}

      {/* Visa knapp för att lägga till underrader om inga finns ännu */}
      {estimatItem.selected && !hasSubItems && (
        <tr className="bg-gray-50">
          <td colSpan="8" className="px-2 sm:px-4 py-1">
            <button
              onClick={() => {
                addSubItem(categoryKey, formItemKey);
                toggleSubItemsExpanded(categoryKey, formItemKey);
              }}
              className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs focus:outline-none rounded px-2 py-1 ml-2 sm:ml-4"
              title="Lägg till underrader för att specificera detaljer"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3">
                <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
              </svg>
              Lägg till underrader
            </button>
          </td>
        </tr>
      )}
    </>
  );
});

EstimatItemRow.displayName = 'EstimatItemRow';

export default EstimatItemRow;
